import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Alert, Breadcrumb, Button, Input, Space, Spin, Table, Typography } from "antd";
import {
  FileOutlined,
  FilePdfOutlined,
  FolderFilled,
  SearchOutlined,
  ExportOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { fetchSharedFolder, getSharedFileUrl } from "../services/publicSharesAPI";
import { formatDate, formatDateLocal } from "../utils/dateUtils";

const SHARE_KINDS = ["drawings", "documents"];

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SharedFolder = () => {
  const { kind, shareToken } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get("folder");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");

  const isValidKind = SHARE_KINDS.includes(kind);
  const isDrawings = kind === "drawings";

  useEffect(() => {
    if (!isValidKind) {
      setLoading(false);
      setError("This link is invalid or no longer shared.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSharedFolder(kind, shareToken, folderId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(
          err?.response?.status === 404
            ? "This link is invalid or no longer shared."
            : "Something went wrong while loading this folder. Please try again."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, shareToken, folderId, isValidKind]);

  useEffect(() => {
    setSearchText("");
  }, [folderId]);

  useEffect(() => {
    document.title = data?.folder?.name
      ? `${data.folder.name} - SubmittalLink`
      : "SubmittalLink";
  }, [data]);

  const openFolder = (id) => {
    const rootId = data?.breadcrumbs?.[0]?.id;
    setSearchParams(id && id !== rootId ? { folder: id } : {});
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const term = searchText.trim().toLowerCase();
    const matches = (value) => !term || (value || "").toLowerCase().includes(term);
    const folders = (data.folders || [])
      .filter((folder) => matches(folder.name))
      .map((folder) => ({ ...folder, key: `folder-${folder.id}`, isFolder: true }));
    const files = (data.files || [])
      .filter((file) => matches(file.name) || matches(file.revision_title))
      .map((file) => ({ ...file, key: `file-${file.id}`, isFolder: false }));
    return [...folders, ...files];
  }, [data, searchText]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => {
        if (record.isFolder) {
          return (
            <Button
              type="link"
              className="p-0"
              icon={<FolderFilled style={{ color: "#faad14" }} />}
              onClick={() => openFolder(record.id)}
            >
              <Typography.Text strong style={{ color: "#7a4a00" }}>
                {name}
              </Typography.Text>
            </Button>
          );
        }
        return (
          <a
            href={getSharedFileUrl(kind, shareToken, record.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            {record.file_type === "application/pdf" ? <FilePdfOutlined /> : <FileOutlined />}
            <span>{name}</span>
          </a>
        );
      },
    },
    ...(isDrawings
      ? [
          {
            title: "Revision Date",
            dataIndex: "revision_date",
            key: "revision_date",
            width: 140,
            responsive: ["md"],
            render: (value, record) => (!record.isFolder && value ? formatDate(value) : ""),
          },
          {
            title: "Revision Title",
            dataIndex: "revision_title",
            key: "revision_title",
            width: 260,
            ellipsis: true,
            responsive: ["md"],
            render: (value, record) => (record.isFolder ? "" : value || ""),
          },
        ]
      : [
          {
            title: "Updated At",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            responsive: ["md"],
            render: (value) => (value ? formatDateLocal(value) : ""),
          },
          {
            title: "Size",
            dataIndex: "file_size",
            key: "file_size",
            width: 110,
            responsive: ["md"],
            render: (value, record) => (record.isFolder ? "" : formatFileSize(value)),
          },
        ]),
    {
      title: "",
      key: "actions",
      width: 140,
      render: (_, record) =>
        record.isFolder ? null : (
          <Space>
            <Button
              icon={<ExportOutlined />}
              href={getSharedFileUrl(kind, shareToken, record.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
            </Button>
            <Button
              icon={<DownloadOutlined />}
              href={getSharedFileUrl(kind, shareToken, record.id, true)}
              title="Download"
            />
          </Space>
        ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-[#001529] text-white">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center text-xl font-bold">
          SubmittalLink
        </div>
      </header>
      <main className="max-w-6xl mx-auto p-4">
        {loading && !data ? (
          <div className="flex justify-center py-16">
            <Spin size="large" />
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-8 max-w-xl mx-auto mt-8">
            <Alert type="warning" showIcon message={error} />
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-4">
            <div className="mb-3">
              {data?.project_name && (
                <div className="text-gray-500 text-sm">{data.project_name}</div>
              )}
              <h1 className="text-xl font-semibold text-gray-900 m-0 break-words">
                {data?.folder?.name}
              </h1>
            </div>
            {data?.breadcrumbs?.length > 1 && (
              <Breadcrumb
                className="mb-3"
                items={data.breadcrumbs.map((crumb, index) => ({
                  key: crumb.id,
                  title:
                    index === data.breadcrumbs.length - 1 ? (
                      crumb.name
                    ) : (
                      <Typography.Link onClick={() => openFolder(crumb.id)}>
                        {crumb.name}
                      </Typography.Link>
                    ),
                }))}
              />
            )}
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search in this folder"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="mb-3 max-w-sm"
            />
            <Table
              columns={columns}
              dataSource={rows}
              loading={loading}
              pagination={rows.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
              locale={{ emptyText: "This folder is empty" }}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default SharedFolder;
